import React, { useEffect, useState } from "react";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { ParticipantView } from "./SpeakerParticipantView";

const MemoizedParticipant = React.memo(
    ParticipantView,
    (prevProps, nextProps) => {
        return prevProps.participantId === nextProps.participantId;
    }
);

function ParticipantGrid({ participantIds, isPresenting, isMeetingContainer }) {
    const { sideBarMode } = useMeetingAppContext();
    const isMobile = window.matchMedia(
        "only screen and (max-width: 768px)"
    ).matches;

    const [participantsPerPage, setParticipantsPerPage] = useState(6);
    const [currentPage, setCurrentPage] = useState(0);
    const [gridCurrentPage, setGridCurrentPage] = useState(0);
    const [gridParticipantsPerPage, setGridParticipantsPerPage] = useState(25);

    // Drag state for pagination panel
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 }); // Will be set to bottom-right in useEffect

    const totalParticipants = participantIds.length;

    useEffect(() => {
        // Only reset pages when participantIds actually change (not just on sidebar toggle)
        setCurrentPage(0);
        setGridCurrentPage(0);
    }, [participantIds]);

    // Handle edge case where current page becomes invalid after participant changes
    useEffect(() => {
        const maxPages = Math.ceil(totalParticipants / gridParticipantsPerPage);
        if (gridCurrentPage >= maxPages && maxPages > 0) {
            setGridCurrentPage(maxPages - 1);
        }
    }, [totalParticipants, gridParticipantsPerPage, gridCurrentPage]);

    useEffect(() => {
        // Handle sidebar mode changes without resetting pages
        if (sideBarMode) {
            setParticipantsPerPage(4);
            setGridParticipantsPerPage(25);
        } else {
            setParticipantsPerPage(6);
            setGridParticipantsPerPage(25);
        }
    }, [sideBarMode]);

    // Set initial position to bottom-right and handle window resize
    useEffect(() => {
        const updatePosition = () => {
            setPanelPosition({
                x: window.innerWidth - 200, // 200px from right edge
                y: window.innerHeight - 80,  // 80px from bottom edge
            });
        };

        // Set initial position
        updatePosition();

        // Handle window resize
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('resize', updatePosition);
        };
    }, []);

    // Drag handlers for pagination panel
    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        const rect = e.currentTarget.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // Constrain to viewport bounds
        const maxX = window.innerWidth - 200; // Approximate panel width
        const maxY = window.innerHeight - 60; // Approximate panel height

        setPanelPosition({
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(0, Math.min(newY, maxY)),
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Add global mouse event listeners when dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none'; // Prevent text selection during drag

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.body.style.userSelect = '';
            };
        }
    }, [isDragging, dragOffset]);

    return (
        <div
            className={`relative flex flex-col md:flex-row flex-grow items-center justify-center ${totalParticipants < 2 && !sideBarMode && !isPresenting
                ? "m-3 md:px-16 md:py-2"
                : totalParticipants < 3 && !sideBarMode && !isPresenting
                    ? "m-3 md:px-16 md:py-8"
                    : totalParticipants < 4 && !sideBarMode && !isPresenting
                        ? "m-3 md:px-16 md:py-4"
                        : totalParticipants >= 5 && totalParticipants <= 6 && !sideBarMode && !isPresenting
                            ? "m-1 md:px-0 md:py-1"
                            : totalParticipants > 4 && !sideBarMode && !isPresenting
                                ? "m-3 md:px-14"
                                : "m-3 md:px-0"
                }`}
        >
            <div className={`flex ${isPresenting ? "flex-row justify-center items-center gap-4" : `flex-col ${
              !isPresenting && totalParticipants >= 5 && totalParticipants <= 6
                ? "justify-center"
                : ""
            }`} w-full h-full`}>
                {/* Left Arrow Icon */}
                {isPresenting && totalParticipants > participantsPerPage && <svg
                    onClick={() => {
                        if (currentPage == 0) {
                            return;
                        }
                        setCurrentPage(currentPage - 1);
                    }}
                    disabled={currentPage === 0}
                    style={{
                        zIndex: 1000,
                        color: "#ffffff",
                        backgroundColor: currentPage == 0 ? "gray" : "#1a1c23",
                        strokeWidth: 4,
                        cursor: "pointer",
                    }}
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-full">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>}

                {isPresenting && (
                    <>
                        {/* Render all participants for audio, but hide non-current page ones */}
                        {participantIds.map((participantId, idx) => {
                            const isOnCurrentPage = idx >= currentPage && idx < currentPage + participantsPerPage;
                            if (!isOnCurrentPage) {
                                return (
                                    <div key={`participant_hidden_${participantId}`} style={{ display: 'none' }}>
                                        <MemoizedParticipant participantId={participantId} />
                                    </div>
                                );
                            }
                            return null;
                        })}

                        {/* Display visible participants with layout */}
                        {participantIds.slice(currentPage, currentPage + participantsPerPage).map((participantId) => {
                            return (
                                <div
                                    key={`participant_${participantId}`}
                                    className={`flex ${isPresenting
                                        ? totalParticipants === 1
                                            ? "md:h-54 md:w-64 xl:w-64 xl:h-54"
                                            : totalParticipants === 2
                                                ? "md:w-64 md:h-54 xl:w-64 xl:h-54"
                                                : totalParticipants === 3
                                                    ? "md:w-64 md:h-54 xl:w-64 xl:h-54"
                                                    : totalParticipants === 4
                                                        ? "md:w-64 md:h-54 xl:w-64 xl:h-54"
                                                        : "md:w-64 md:h-54 xl:w-64 xl:h-54"
                                        : "w-full"
                                        } items-center justify-center ${totalParticipants <= 2
                                            ? "md:max-w-[400px] 2xl:max-w-[400px] "
                                            : "md:max-w-lg 2xl:max-w-2xl"
                                        } overflow-hidden p-1`}
                                    style={!isPresenting ? { aspectRatio: '16/9' } : {}}
                                >
                                    <MemoizedParticipant participantId={participantId} />
                                </div>
                            );
                        })}
                    </>
                )}
                {/* Right Arrow Icon */}
                {isPresenting && totalParticipants > participantsPerPage && <svg
                    onClick={() => {
                        if (currentPage + participantsPerPage >= totalParticipants) {
                            return;
                        }
                        setCurrentPage(currentPage + 1);
                    }}
                    disabled={currentPage + participantsPerPage >= totalParticipants}
                    style={{
                        zIndex: 1000,
                        color: "#ffffff",
                        backgroundColor: (currentPage + participantsPerPage >= totalParticipants) ? "gray" : "#1a1c23",
                        strokeWidth: 4,
                        cursor: "pointer",

                    }}
                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-full">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5 15.75 12l-7.5 7.5" />
                </svg>}
                {!isPresenting && (() => {
                    // Calculate paginated participants for grid view
                    const startIndex = gridCurrentPage * gridParticipantsPerPage;
                    const endIndex = startIndex + gridParticipantsPerPage;
                    const paginatedParticipants = participantIds.slice(startIndex, endIndex);

                    // Calculate perRow based on current page participants, not total participants
                    const currentPageParticipants = paginatedParticipants.length;
                    const perRow =
                        isMobile || isPresenting
                            ? currentPageParticipants < 4
                                ? 1
                                : currentPageParticipants < 9
                                    ? 2
                                    : 3
                            : currentPageParticipants === 2
                                ? 2  // For 2 participants, use 2 columns (side by side)
                                : currentPageParticipants < 5
                                    ? 2
                                    : currentPageParticipants < 7
                                        ? 3
                                        : currentPageParticipants < 9
                                            ? 4
                                            : currentPageParticipants < 10
                                                ? 3
                                                : currentPageParticipants < 13
                                                    ? 4
                                                    : 5;

                    return (
                        <>
                            {/* Render all participants for audio, but hide non-current page ones */}
                            {participantIds.map((participantId, idx) => {
                                const isOnCurrentPage = idx >= startIndex && idx < endIndex;
                                if (!isOnCurrentPage) {
                                    return (
                                        <div key={`participant_hidden_${participantId}`} style={{ display: 'none' }}>
                                            <MemoizedParticipant participantId={participantId} />
                                        </div>
                                    );
                                }
                                return null;
                            })}

                            {/* Display visible participants with layout */}
                            {(() => {
                                // Special handling for 2 participants - display them side by side (half width each)
                                // Apply this only on non-mobile screens; on mobile, stack vertically
                                if (
                                    (totalParticipants === 2 || currentPageParticipants === 2) &&
                                    !isMobile
                                ) {
                                    return (
                                        <div className="flex flex-1 items-center justify-center h-full w-full gap-4">
                                            {paginatedParticipants.map((participantId, index) => {
                                                return (
                                                    <div
                                                        key={`participant_${participantId}`}
                                                        className="flex w-1/2 h-full items-center justify-center overflow-hidden p-0"
                                                    >
                                                        <MemoizedParticipant
                                                            participantId={participantId}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }

                                return (
                                    <div className={`flex flex-col h-full w-full ${
                                        currentPageParticipants >= 5 && currentPageParticipants <= 6
                                          ? "gap-1 justify-center items-center"
                                          : "gap-4"
                                      }`}>
                                        {Array.from(
                                            { length: Math.ceil(paginatedParticipants.length / perRow) },
                                            (_, i) => {
                                                const rowParticipants = paginatedParticipants.slice(i * perRow, (i + 1) * perRow);
                                                const isLastRowWithOneParticipant = i === Math.ceil(paginatedParticipants.length / perRow) - 1 && rowParticipants.length === 1 && currentPageParticipants === 3;
                                                
                                                return (
                                                    <div
                                                        key={`participant-${i}`}
                                                        className={`flex flex-1 items-center justify-center gap-4`}
                                                    >
                                                        {rowParticipants.map((participantId) => {
                                                            // Calculate width class based on perRow for non-presenting mode
                                                            const getWidthClass = () => {
                                                                // For 3 participants, second row has 1 participant - make it half width to center it
                                                                if (isLastRowWithOneParticipant) return "w-1/2";
                                                                if (perRow === 2) return "w-1/2";
                                                                if (perRow === 3) return "w-1/3";
                                                                if (perRow === 4) return "w-1/4";
                                                                if (perRow === 5) return "w-1/5";
                                                                return "w-full";
                                                            };

                                                            return (
                                                                <div
                                                                    key={`participant_${participantId}`}
                                                                    className={`flex ${getWidthClass()} items-center ${paginatedParticipants.length <= 3
                                                                        ? "justify-start"
                                                                        : "justify-center"
                                                                        } h-full ${paginatedParticipants.length === 1
                                                                            ? "md:max-w-full 2xl:max-w-full "
                                                                            : "md:max-w-full 2xl:max-w-full"
                                                                        } overflow-hidden ${
                                                                          currentPageParticipants >= 5 && currentPageParticipants <= 6
                                                                            ? "p-1 flex-grow"
                                                                            : perRow === 2 || perRow === 3 ? "p-0" : "p-1"
                                                                        }`}
                                                                >
                                                                    <MemoizedParticipant
                                                                        participantId={participantId}
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    );
                })()}
            </div>

            {/* Grid Pagination Controls - Only for non-presenting mode */}
            {!isPresenting && (() => {
                if (totalParticipants <= gridParticipantsPerPage) {
                    return null;
                }

                return (
                    <div
                        className={`fixed flex items-center gap-3 z-[9999] bg-gray-900/90 backdrop-blur-sm rounded-full px-5 py-3 shadow-2xl border border-gray-700 transition-all duration-200 ${isDragging ? 'cursor-grabbing scale-105 shadow-3xl' : 'cursor-grab'
                            }`}
                        style={{
                            left: `${panelPosition.x}px`,
                            top: `${panelPosition.y}px`,
                        }}
                        onMouseDown={handleMouseDown}
                    >
                        {/* Previous Button */}
                        <button
                            onClick={() => {
                                if (gridCurrentPage > 0) {
                                    setGridCurrentPage(gridCurrentPage - 1);
                                }
                            }}
                            disabled={gridCurrentPage === 0}
                            className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200 ${gridCurrentPage === 0
                                ? 'bg-gray-700 cursor-not-allowed opacity-40'
                                : 'bg-[#7700ff] hover:bg-[#9933ff] cursor-pointer'
                                }`}
                            style={{
                                color: '#ffffff',
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                            </svg>
                        </button>

                        {/* Page Counter */}
                        <div className="flex items-center gap-1 text-white text-sm font-medium px-2">
                            <span>{gridCurrentPage + 1}</span>
                            <span className="text-gray-400">/</span>
                            <span>{Math.ceil(totalParticipants / gridParticipantsPerPage)}</span>
                        </div>

                        {/* Next Button */}
                        <button
                            onClick={() => {
                                const totalPages = Math.ceil(totalParticipants / gridParticipantsPerPage);
                                if (gridCurrentPage < totalPages - 1) {
                                    setGridCurrentPage(gridCurrentPage + 1);
                                }
                            }}
                            disabled={gridCurrentPage >= Math.ceil(totalParticipants / gridParticipantsPerPage) - 1}
                            className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200 ${gridCurrentPage >= Math.ceil(totalParticipants / gridParticipantsPerPage) - 1
                                ? 'bg-gray-700 cursor-not-allowed opacity-40'
                                : 'bg-[#7700ff] hover:bg-[#9933ff] cursor-pointer'
                                }`}
                            style={{
                                color: '#ffffff',
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5 15.75 12l-7.5 7.5" />
                            </svg>
                        </button>
                    </div>
                );
            })()}
        </div >
    );
}

export const MemoizedParticipantGrid = React.memo(
    ParticipantGrid,
    (prevProps, nextProps) => {
        return (
            JSON.stringify(prevProps.participantIds) ===
            JSON.stringify(nextProps.participantIds) &&
            prevProps.isPresenting === nextProps.isPresenting
        );
    }
);